/** Local-only retinotopic sampling. Caller supplies ImageData and owns camera lifecycle.
 * No network, persistence, media capture, or DOM dependencies in this module.
 * UVs have top-left origin, x right, y down, and apply to a square 24-degree field.
 */
export function sampleRetinotopy(image, uv, mask, options = {}) {
  const { mirror = false, channel = 'luminance', out } = options;
  if (!['luminance', 'rgb'].includes(channel)) throw new Error('Unknown sample channel');
  const { width: w, height: h, data } = image;
  if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1 || data.length !== w * h * 4) throw new Error('Invalid ImageData');
  if (uv.length !== 2 * mask.length) throw new Error('UV/mask size mismatch');
  const stride = channel === 'rgb' ? 4 : 1;
  const values = out ?? new Float32Array(mask.length * stride);
  if (!(values instanceof Float32Array) || values.length !== mask.length * stride) throw new Error('Wrong output shape');
  values.fill(0);
  // Centre-crop to a square: equal degrees per source pixel on both axes.
  const side = Math.min(w, h), ox = (w - side) / 2, oy = (h - side) / 2;
  for (let i = 0; i < mask.length; i++) {
    if (!mask[i]) continue;
    const u = mirror ? 1 - uv[2 * i] : uv[2 * i], v = uv[2 * i + 1];
    if (!Number.isFinite(u) || !Number.isFinite(v) || u < 0 || u > 1 || v < 0 || v > 1) continue;
    const x = ox + u * (side - 1), y = oy + v * (side - 1);
    const x0 = Math.floor(x), y0 = Math.floor(y), x1 = Math.min(x0 + 1, w - 1), y1 = Math.min(y0 + 1, h - 1);
    const a = x - x0, b = y - y0;
    const indices = [4 * (y0 * w + x0), 4 * (y0 * w + x1), 4 * (y1 * w + x0), 4 * (y1 * w + x1)];
    const weights = [(1-a)*(1-b), a*(1-b), (1-a)*b, a*b];
    const rgb = [0, 0, 0];
    for (let k = 0; k < 4; k++) for (let c = 0; c < 3; c++) rgb[c] += weights[k] * data[indices[k] + c] * data[indices[k] + 3] / 65025;
    if (channel === 'rgb') {
      values.set(rgb, 4 * i); values[4 * i + 3] = 1;
    } else {
      // Display luma in sRGB, intentionally not radiometric or a neural response.
      values[i] = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
    }
  }
  return values;
}
